import gql from 'graphql-tag'
import { BASIC_PROGRAM_DATA } from './programQuery'

export const BASIC_ACTIVITY_DATA = `
  id
  name
  slug
  productLine
  createdAt
  updatedAt
  images { url: cardUrl, primary, caption }
  position
  state
  isPrivate
`

export const ALGOLIA_ACTIVITY_DATA = `
  id
  name
  image
`

export const ALGOLIA_LESSON_DATA = `
  id
  name
  image
  slug
`

export const BASIC_LESSON_DATA = `
  id
  name
  slug
  productLine
  createdAt
  updatedAt
  images { url: cardUrl,  primary, caption }
  position
  state
`

export const FEEDBACK_DATA = `
  like
  comment
`

export const FULL_LESSON_DATA = `
  ${BASIC_LESSON_DATA}
  objectives
  standards
  educationStandards
  description
  isCsf
  state
  videos {
    url
    title
  }
  materials {
    name
    quantity
  }
  steps {
    id
    position
    title
    description
    educatorTip
    images {
      url
      id
      caption
    }
    videos {
      embedKey
      id
      url
    }
    question {
      id
      position
      questionText
      questionType
      answers {
        id
        position
        choice
        isCorrect
        title
      }
    }
  }
  kits {
    product {
      name
    }
  }
  products {
    product {
      id
      name
    }
    quantity
  }
  tags {
    themes
    grades
    subjects
  }
  supplies
  language
  details
  standards
  user {
    username
    email
    firstName
    lastName
    subscriptions {
      activationCode
    }
  }
  duration
  links {
    groupType
    links {
      title
      url
    }
  }
  files {
    title
    url
    poster
  }
}
`

export const COLLECTION_DATA = `
  ${BASIC_LESSON_DATA}
  objectives
  standards
  educationStandards
  description
  isCsf
  state
  videos {
    url
    title
  }
  materials {
    name
    quantity
  }
  steps {
    id
    position
    title
    description
    educatorTip
    images {
      url
      id
      caption
    }
    videos {
      embedKey
      id
      url
    }
    question {
      id
      position
      questionText
      questionType
      answers {
        id
        position
        choice
        isCorrect
        title
      }
    }
  }
  kits {
    product {
      name
    }
  }
  products {
    product {
      id
      name
    }
    quantity
  }
  tags {
    themes
    grades
    subjects
  }
  supplies
  language
  details
  standards
  user {
    username
    email
    firstName
    lastName
    subscriptions {
      activationCode
    }
  }
  duration
  links {
    groupType
    links {
      title
      url
    }
  }
  files {
    title
    url
    poster
  }
  }
`

export const FULL_ACTIVITY_DATA = `
  ${BASIC_ACTIVITY_DATA}
  standards
  description
  courses {
    name
  }
  currentAssignment {
    slug
    startedOn
    progress
  }
  isCsf
  state
  isSubscribed
  steps {
    id
    position
    title
    description
    educatorTip
    images {
      url
      id
      caption
    }
    videos {
      embedKey
      id
    }
    files {
      url
      id
      name
    }
    question {
      id
      position
      questionText
      questionType
      answers {
        id
        position
        choice
        isCorrect
        title
      }
    }
  }
  products {
    id
    name
    eduMultiparenttagPk
  }
  tags {
    themes
  }
  grades
  supplies {
    name
  }
  language {
    id
    name
  }
  user {
    username
    email
    firstName
    lastName
    subscriptions {
      activationCode
    }
  }
  duration
  program {
    ${BASIC_PROGRAM_DATA}
  }
}
`

const ASSIGNMENT_DATA = `
  startedOn
  slug
  id
  progress
  createdAt
  completedOn
  acceptedOn
  instructor {
    username
  }
  tasks {
    id
    response
    image {
      url
      id
    }
    state
    step
    {
    __typename
    ...on SpheroStep {
      id
    }
    }
  }
  user {
    id
    username
    firstName
    lastName
    email
  }
  program {
    ${BASIC_PROGRAM_DATA}
  }
  activity {
    ${FULL_ACTIVITY_DATA}
`

export const COURSES_DATA = `
  id
  description
  images {
    url
    cardUrl
    caption
    primary
  }
  products {
    name
  }
  tags {
    grades
  }
  name
  position
  activityCount
  lessonCount
`

export const ACTIVITIES_QUERY = gql`
  query ActivityByName($name: String) {
    activityByName(name: $name) {
      ${BASIC_ACTIVITY_DATA}
    }
  }
`

export const ACTIVITY_DEFINITION = gql`
  query Activity($slug: String!, $queryId: String, $language: String) {
    activity(slug: $slug, queryId: $queryId, language: $language) {
      ${FULL_ACTIVITY_DATA}
    }
`

export const LESSON_DEFINITION = gql`
  query lesson($slug: String!, $queryId: String) {
    lesson(slug: $slug, queryId: $queryId) {
      ${FULL_LESSON_DATA}
    }
`

export const LESSON_FEEDBACK = gql`
  query feedback($userId: Int!, $projectableId: Int!, $projectableType: String!) {
    feedback(userId: $userId, projectableId: $projectableId, projectableType: $projectableType) {
      ${FEEDBACK_DATA}
    }
  }
`

export const UPSERT_FEEDBACK = gql`
  mutation upsertFeedback($projectableId: Int!, $projectableType: String!, $comment: String, $recommend: Int, $like: Boolean) {
    upsertFeedback(projectableId: $projectableId, projectableType: $projectableType, like: $like, comment: $comment, recommend: $recommend) {
      ${FEEDBACK_DATA}
    }
  }
`

export const COLLECTION_DEFINITION = gql`
  query Course($id: Int!) {
    course(id: $id) {
      activities {
        position
        activity {
          id
          name
          slug
          images {
            url
          }
        }
      }
      lessons {
        position
        lesson {
          id
          name
          slug
          images {
            url
          }
        }
      }
      name
      description
      productLine
      documentLinks { url }
      images {
        url
      }
      tags {
        grades
      }
      products {
        name
      }
    }
  }
`

export const SAVE_ACTIVITY = gql`
  mutation upsertActivity(
    $id: Int,
    $slug: String,
    $name: String!,
    $description: String!,
    $duration: String,
    $productLine: String,
    $standards: [Int!],
    $tags: LessonTagInput,
    $supplies: [SupplyInput!],
    $steps: [StepInput!],
    $isPrivate: Boolean,
    $programs: [ProgramInput!],
    $products: [SpheroProductInput!],
    $image: Upload,
    $grades: [String!]!,
    $eduLanguageId: Int
  ) {
    upsertActivity(
      id: $id,
      slug: $slug,
      name: $name,
      description: $description,
      duration: $duration,
      productLine: $productLine,
      standards: $standards,
      tags: $tags,
      supplies: $supplies,
      steps: $steps,
      isPrivate: $isPrivate,
      programs: $programs,
      products: $products,
      image: $image,
      grades: $grades,
      eduLanguageId: $eduLanguageId
    ) {
      status
      activity {
        ${FULL_ACTIVITY_DATA}
      errors
    }
  }
`

export const REMOVE_ACTIVITY = gql`
  mutation RemoveLesson($lessonSlug: String!, $curriculumId: Int!) {
    removeLesson(lessonSlug: $lessonSlug, curriculumId: $curriculumId) {
      status
      errors
      lesson {
        id
        name
        slug
        groupName
      }
    }
  }
`

export const ACTIVITIES_BY_FILTERS = gql`
  query activitiesByFilters($featured: Boolean, $sphero: Boolean, $community: Boolean, $csf: Boolean, $productLine: String, $sortOrder: String, $page: Int, $pageSize: Int, $language: String, $gradeLevels: String, $name: String, $state: String, $standards: String, $themes: String, $duration: String, $products: String) {
    activitiesByFilters(featured: $featured, sphero: $sphero, community: $community, csf: $csf, productLine: $productLine, sortOrder: $sortOrder, page: $page, pageSize: $pageSize, language: $language, gradeLevels: $gradeLevels, name: $name, state: $state, standards: $standards, themes: $themes, duration: $duration, products: $products ) {
      totalCount
      activities {${BASIC_ACTIVITY_DATA}},
    }
  }`

export const ACTIVITIES_BY_TERMS = gql`
  query activitiesByTerms($sphero: Boolean, $community: Boolean, $page: Int, $pageSize: Int, $searchTerm: String, $gradeLevels: String, $programType: String, $products: String) {
    activitiesByTerms(sphero: $sphero, community: $community, page: $page, pageSize: $pageSize, gradeLevels: $gradeLevels, programType: $programType, searchTerm: $searchTerm, products: $products ) {
      totalCount
      queryId
      activities {${ALGOLIA_ACTIVITY_DATA}},
    }
  }`

export const LESSONS_BY_FILTERS = gql`
  query lessonsByFilters($featured: Boolean, $sphero: Boolean, $community: Boolean, $eduReady: Boolean, $productLine: String, $sortOrder: String, $page: Int, $pageSize: Int, $name: String) {
    lessonsByFilters(featured: $featured, sphero: $sphero, community: $community, eduReady: $eduReady, productLine: $productLine, sortOrder: $sortOrder, page: $page, pageSize: $pageSize, name: $name ) {
      totalCount
      lessons {${BASIC_LESSON_DATA}},
    }
  }`

export const LESSONS_BY_TERMS = gql`
  query lessonsByTerms($page: Int, $pageSize: Int, $searchTerm: String, $gradeLevel: String, $productLine: String, $products: String, $sortOrder: String) {
    lessonsByTerms(page: $page, pageSize: $pageSize, searchTerm: $searchTerm, gradeLevel: $gradeLevel, productLine: $productLine, products: $products, sortOrder: $sortOrder ) {
      totalCount
      queryId
      activities {${ALGOLIA_LESSON_DATA}},
    }
  }`

export const COURSES_BY_FILTERS = gql`
  query coursesByFilters($page: Int, $pageSize: Int, $productLine: String, $name: String, $products: [String!], $tags: [String!], $featured: String, $sortOrder: String, $eduReady: Boolean ) {
    coursesByFilters(page: $page, pageSize: $pageSize, productLine: $productLine, name: $name, products: $products, tags: $tags, featured: $featured, sortOrder: $sortOrder, eduReady: $eduReady) {
      totalCount
      courses {${COURSES_DATA}}
    }
  }`

export const MY_ACTIVITIES = gql`
  query activities($status: String, $gradeLevel: String, $language: String) {
    activities(status: $status, gradeLevel: $gradeLevel, language: $language) {
      ${BASIC_ACTIVITY_DATA},
    }
  }
`

export const MY_ASSIGNMENTS = gql`
  query myAssignments($page: Int, $pageSize: Int, $orderBy: Int) {
      assignments(page: $page, pageSize: $pageSize, orderBy: $orderBy) {
      totalCount
      assignments {
        startedOn
        slug
        id
        progress
        updatedAt
        activity {
          ${BASIC_ACTIVITY_DATA}
        }
      }
    }
  }
`

export const ASSIGNMENTS_BY_USER = gql`
  query assignmentsByUser($userId: Int!, $classroomId: Int, $activityId: Int, $page: Int, $pageSize: Int, $orderBy: Int) {
    assignmentsByUser(userId: $userId, classroomId: $classroomId, activityId: $activityId, page: $page, pageSize: $pageSize, orderBy: $orderBy) {
      assignments {
        ${ASSIGNMENT_DATA}
      }
      totalCount
    }
  }
`

export const CREATE_ACTIVITY = gql`
  mutation CreateLesson($lessonName: String!, $lessonDescription: String) {
    createLesson(lessonName: $lessonName, lessonDescription: $lessonDescription) {
      slug
      id
    }
  }
`

export const DELETE_ACTIVITY = gql`
  mutation deleteActivity($id: Int!) {
    deleteActivity(id: $id) {
      errors
      status
      activity {
        ${BASIC_ACTIVITY_DATA}
      }
    }
  }
  `

export const FILTER_OPTIONS = gql`
  query GetOptions {
    options {
      standards
      grades
      languages {
        id
        name
      }
    }
  }`

export const START_ACTIVITY = gql`
  mutation StartActivity($slug: String!) {
    startActivity(slug: $slug) {
        slug
        id
    }
  }`

export const START_ASSIGNMENT = gql`
  mutation StartAssignment($slug: String!) {
    startAssignment(slug: $slug) {
        slug
        id
        startedOn
        user {
          username
        }
        activity {
          ${FULL_ACTIVITY_DATA}
    }
  }`

export const COMPLETE_ASSIGNMENT = gql`
  mutation CompleteAssignment ($slug: String!) {
    completeAssignment (slug: $slug) {
      ${ASSIGNMENT_DATA}
    }
  }`

export const ASSIGNMENT = gql`
  query Assignment($slug: String!) {
    assignment(slug: $slug) {
        ${ASSIGNMENT_DATA}
    }
  }`

export const REMOVE_PROGRAM_FROM_ACTIVITY = gql`
  mutation RemoveProgramFromActivity($programId: Int!, $activityId: Int!) {
    removeProgramFromActivity(programId: $programId, activityId: $activityId) {
      errors
      status
      activity {
        ${FULL_ACTIVITY_DATA}
    }
  }`

export const PUBLISH_ACTIVITY = gql`
  mutation PublishActivity($slug: String!, $isPrivate: Boolean) {
    publishActivity(slug: $slug, isPrivate: $isPrivate) {
      errors
      status
      activity {
        ${FULL_ACTIVITY_DATA}
      }
    }`

export const PUBLISH_ACTIVITY_SLUG = gql`
  mutation PublishActivity($slug: String!, $isPrivate: Boolean) {
    publishActivity(slug: $slug, isPrivate: $isPrivate) {
      errors
      status
      activity {
        slug
      }
    }
  }`

export const REJECT_ACTIVITY = gql`
  mutation RejectActivity($slug: String!) {
    rejectActivity(slug: $slug) {
      errors
      status
      activity {
        ${FULL_ACTIVITY_DATA}
      }
    }`

export const REJECT_ACTIVITY_SLUG = gql`
  mutation RejectActivity($slug: String!) {
    rejectActivity(slug: $slug) {
      errors
      status
      activity {
        slug
      }
    }
  }`

export const SUMBIT_FOR_REVIEW = gql`
  mutation SubmitForReview($slug: String!) {
    submitForReviewActivity(slug: $slug) {
      errors
      status
      activity {
        ${FULL_ACTIVITY_DATA}
      }
    }`

export const REPLACE_PROGRAM = gql`
    mutation AssignProgram($assignmentId: Int!, $programId: Int!) {
      assignProgram(assignmentId: $assignmentId, programId: $programId) {
        assignment {
          ${ASSIGNMENT_DATA}
        }
        errors
        status
      }
    }
`

export const PREMIUM_CONTENT = gql`
  query PremiumContent {
    premiumContent {
      ${BASIC_ACTIVITY_DATA}
    }
  }
`
